import React, { useState, useEffect, useCallback } from 'react';
import aiTipsService from '../services/aiTipsService';

const AiTipPopup = ({ 
    tip, 
    onDismiss, 
    onFeedback,
    chefPosition = { edge: 'bottom', offset: 24 }
}) => {
    const [feedbackGiven, setFeedbackGiven] = useState(false);
    
    // Dragging state
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [position, setPosition] = useState(() => {
        // Calculate initial position based on chef position
        if (chefPosition.edge === 'left') {
            return { x: 100, y: chefPosition.offset };
        } else {
            return { x: chefPosition.offset, y: window.innerHeight - 380 };
        }
    });

    const colorTokens = {
        oxfordBlue: '#1b263b',
        neutral: {
            0: '#FFFFFF',
            50: '#F9F8F6',
            900: '#0D0D0D'
        }
    };

    const handleDismiss = () => {
        onDismiss();
    };

    const handleFeedback = async (helpful) => {
        setFeedbackGiven(true);
        await onFeedback(helpful);
        // Auto-dismiss after feedback
        setTimeout(handleDismiss, 1500);
    };

    const priorityInfo = aiTipsService.getPriorityInfo(tip.priority);
    const typeIcon = aiTipsService.getTypeIcon(tip.type);

    // Dragging functionality
    const handleMouseDown = useCallback((e) => {
        // Only drag from title bar
        if (e.target.closest('.title-bar')) {
            setIsDragging(true);
            setDragOffset({
                x: e.clientX - position.x,
                y: e.clientY - position.y
            });
        }
    }, [position]);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging) return;
            
            setPosition({
                x: e.clientX - dragOffset.x,
                y: e.clientY - dragOffset.y
            });
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragOffset]);

    return (
        <div 
            className="fixed z-[9998] bg-neutral-0 w-96 shadow-popup border border-black"
            style={{
                left: `${position.x}px`,
                top: `${position.y}px`,
                cursor: isDragging ? 'grabbing' : 'default'
            }}
            onMouseDown={handleMouseDown}
        >
            {/* Title Bar */}
            <div className="title-bar bg-neutral-0 text-neutral-900 px-4 py-2 flex justify-between items-center cursor-grab active:cursor-grabbing select-none hairline-b">
                <div className="flex items-center space-x-2">
                    <span className="text-lg">{typeIcon}</span>
                    <span 
                        className="font-mono uppercase tracking-wider font-medium truncate"
                        style={{ 
                            color: priorityInfo.color,
                            fontSize: tip.priority === 'critical' ? '16px' : '12px'
                        }}
                    >
                        {priorityInfo.name}
                    </span>
                </div>
                <button 
                    onClick={handleDismiss}
                    className="text-neutral-900 text-base flex-shrink-0 border-none bg-transparent hover:bg-transparent focus:bg-transparent active:bg-transparent hover:text-neutral-500 transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Content Area */}
            <div className="p-4">
                {/* Content */}
                <div className="mb-3">
                    <p className="text-sm text-neutral-900 leading-relaxed">
                        {tip.content}
                    </p>
                </div>

                {/* Source Context */}
                {tip.sourceContext && (
                    <div className="text-xs text-neutral-900 opacity-60 mb-3 font-mono">
                        {tip.sourceContext}
                    </div>
                )}

                {/* Feedback Buttons */}
                {!feedbackGiven ? (
                    <div className="flex items-center space-x-2 pt-3 border-t border-neutral-900 border-opacity-20">
                        <span className="text-xs text-neutral-900 opacity-60 font-mono uppercase tracking-wider">
                            Helpful?
                        </span>
                        <button
                            onClick={() => handleFeedback(true)}
                            className="px-2 py-1 text-xs text-white hover:opacity-60 transition-opacity font-mono border-none"
                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                        >
                            Yes
                        </button>
                        <button
                            onClick={() => handleFeedback(false)}
                            className="px-2 py-1 text-xs bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono"
                        >
                            No
                        </button>
                    </div>
                ) : (
                    <div className="pt-3 border-t border-neutral-900 border-opacity-20">
                        <span className="text-xs text-neutral-900 opacity-60 font-mono">
                            ✓ Thank you for your feedback
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AiTipPopup;

